/// <amd-module name="Wit/WorkItemForm/Common/Components/FieldValue" />
import { dateToString } from "VSS/Core/Util/String";
import { usePageContext } from "VSS/Platform/Layout";
import { Persona, PersonaSize } from "VSSUI/Persona";
import { Pill, PillSize } from "VSSUI/Pill";
import { CommentFormat } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { CoreFieldRefNames, FieldType } from "Wit/Common/Generated/Constants";
import { HtmlPreview } from "Wit/HtmlEditor/Components/HtmlPreview";
import {
    convertWorkItemIdentityRefFromFieldValue,
    isIdentityRef,
    isWorkItemIdentityRef,
    stripIdPartFromDisplayName
} from "Wit/Identity/Utilities/WorkItemIdentityHelper";
import { WorkItemFormCommentPreview } from "Wit/WorkItemForm/Details/WorkItemFormCommentPreview";
import * as React from "react";
import { useFieldMetadata } from "../Hooks";
import { StateCircle } from "./StateCircle";
export interface IFieldValueProps {
    refName: string;
    fieldValue: unknown;
}
export interface ICommentFieldValueProps extends IFieldValueProps {
    commentFormat: CommentFormat;
}

export function FieldValue({ fieldValue, refName }: IFieldValueProps) {
    const fieldMetadata = useFieldMetadata(refName);
    if (fieldValue === null || fieldValue === undefined) {
        return null;
    }

    if (refName === CoreFieldRefNames.State && typeof fieldValue === "string") {
        return (
            <span>
                <StateCircle className="icon-margin" state={fieldValue} role="presentation" />
                {fieldValue}
            </span>
        );
    }

    if (refName === CoreFieldRefNames.Tags && typeof fieldValue === "string") {
        const tags = fieldValue.split(";").map(t => t.trim());
        return (
            <span className="inline-flex-row">
                {tags.map((tag, i) => (
                    <Pill key={i} size={PillSize.compact}>
                        {tag}
                    </Pill>
                ))}
            </span>
        );
    }

    if ((fieldMetadata?.type === FieldType.PlainText || fieldMetadata?.type === FieldType.Html || fieldMetadata?.type === FieldType.History) && typeof fieldValue === "string") {
        return <HtmlPreview rawHtml={fieldValue} />;
    }

    if (typeof fieldValue === "string" || typeof fieldValue === "number") {
        return <span>{fieldValue}</span>;
    }

    if (typeof fieldValue === "boolean") {
        return <span>{fieldValue ? "True" : "False"}</span>;
    }

    if (fieldValue instanceof Date) {
        return <span>{dateToString(fieldValue)}</span>;
    }

    if (fieldMetadata?.isIdentity) {
        return <IdentityFieldValue fieldValue={fieldValue} />;
    }

    return (
        <span>
            Field {refName} has value {JSON.stringify(fieldValue)} that is not yet supported
        </span>
    );
}

export function CommentFieldValue({ fieldValue, refName, commentFormat }: ICommentFieldValueProps) {
    const fieldMetadata = useFieldMetadata(refName);
    if (fieldValue === null || fieldValue === undefined) {
        return null;
    }

    if ((fieldMetadata?.type === FieldType.Html || fieldMetadata?.type === FieldType.History) && typeof fieldValue === "string") {
        if (commentFormat === CommentFormat.Markdown) {
            return <WorkItemFormCommentPreview content={fieldValue} />;
        } else {
            return <HtmlPreview rawHtml={fieldValue} />;
        }
    }

    return (
        <span>
            Comment field {refName} has value {JSON.stringify(fieldValue)} and format {JSON.stringify(commentFormat)} that is not yet supported
        </span>
    );
}

function IdentityFieldValue({ fieldValue }: { fieldValue: unknown }) {
    const pageContext = usePageContext();

    if (typeof fieldValue !== "string" && !isIdentityRef(fieldValue) && !isWorkItemIdentityRef(fieldValue)) {
        return null;
    }
    const identityRef = isWorkItemIdentityRef(fieldValue)
        ? fieldValue.identityRef
        : isIdentityRef(fieldValue)
        ? convertWorkItemIdentityRefFromFieldValue(pageContext, fieldValue.displayName).identityRef
        : convertWorkItemIdentityRefFromFieldValue(pageContext, fieldValue).identityRef;

    const displayName = stripIdPartFromDisplayName(identityRef.displayName);

    return (
        <div className="inline-flex-row flex-baseline identity-field-value">
            <Persona className="icon-margin" size={PersonaSize.size16} identity={identityRef} />
            <div>{displayName}</div>
        </div>
    );
}
